// Global Instructions Rule Applied!
//
// Fund Transactions booking screen.
//
// Type-driven form: header (fund / type / date / description) + line-items
// grid. Every save round-trips through POST /fund-transactions, which
// regenerates the journal preview server-side and returns the full detail -
// the preview shown here is always the persisted preview. Manual journal
// lines are editable/addable/deletable and persist via the save payload's
// manual_journals replacement set; auto lines are derived from items and
// read-only (they regenerate on save). Posting is gated on a balanced,
// non-empty journal set (server re-checks).
//
// Layout is cfg-driven: cfg.fund_transaction_types.journals_separate_tab
// renders the journal preview on a second tab (complex types) or inline
// below the items (simple types).
//
// Closing types (cfg.fund_transaction_types.closes_commitments) swap the
// line items + journal preview for a Commitments checklist (and, on a fund
// on the Financial year basis, the informational Earlier years section -
// ClosingEarlierYearsSection.tsx): the ticked
// commitments persist with Save (commitment_ids replacement set) and Post
// sets them Closed with date_closed = the transaction date; Unbook reopens
// them. Rows already closed with a date by something else are greyed out;
// legacy rows that are Closed with no date are candidates but start
// unticked. Standalone funds only (the server refuses consolidated members).
// New records accept ?fund=&type= prefill (Investors tab "Close
// Commitments", the Fund Transactions "+ New" menu).
//
// Transfer of Interest types (cfg.fund_transaction_types.transfers_interest,
// spec A3.6 / A3.7) swap them for a Transfer section: the commitment being
// assigned (candidates read), ONE ROW PER TRANSFEREE (an existing investor
// or a new one under an account, the amount it takes, the terms it takes -
// prefilled from the transferor, editable - and a memo consideration), and
// a read-only "Position at transfer date" card (preview read while Draft;
// the stamped snapshot once Booked) with each transferee's share. A single
// transferee taking the whole commitment re-points it (A3.6); anything else
// splits it into child commitments at Post (A3.7). No journals either way;
// Unbook hands it back. ?commitment= prefill comes from the Investors tab
// row menu.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import DatePicker from '../../../../core/components/DatePicker.tsx'
import { DeleteOutlined, DownloadOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { store } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { removeTab, transformFormTab, updateTabTitle } from '../../../../store/slices/tabSlice'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { TABLE_CAPITAL_CALLS, TABLE_DISTRIBUTIONS, TABLE_FUND_TRANSACTIONS } from '../../../../state_manager/globals.ts'
import {
  decimalToPercent,
  percentToDecimal,
  thousandsProps,
  useNumberFormat,
  useNumberDisplay
} from '../../../../core/utils/inputNumberFormat.ts'
import {
  FundTransactionDetail,
  FundTransactionSavePayload
} from '../../../../state_manager/data_models/fund_transaction_detail.ts'
import { fetchJournalEntryOptions } from '../../../journal_entries/services/journalEntriesService.ts'
import EditContributionModal from '../../../capital_calls/components/EditContributionModal.tsx'
import ClosingEarlierYearsSection from '../ClosingEarlierYearsSection.tsx'
import {
  fetchFundTransactionDetail,
  fetchFundClosingCandidates,
  fetchFundTransferCandidates,
  fetchFundTransferPreview,
  saveFundTransaction,
  bookFundTransaction,
  unbookFundTransaction,
  trashFundTransaction
} from '../../services/fundTransactionsService.ts'
import type { FundClosingCandidateRow } from '../../../../state_manager/data_models/fund_closing_candidate.ts'
import type { FundTransferCandidateRow } from '../../../../state_manager/data_models/fund_transfer_candidate.ts'
import {
  FundTransferPreviewSchema,
  type FundTransferPreview
} from '../../../../state_manager/data_models/fund_transfer_preview.ts'
import { useFundCommitmentEditorOptions } from '../../../funds/hooks/useFundCommitmentEditorOptions.ts'
import { searchCompaniesForPopup } from '../../../companies/services/companiesService.ts'
import { exportJsonToExcel } from '../../../../core/services/utilitiesService.ts'
import {
  updateFundTransactionRecord,
  removeFundTransaction
} from '../../../../state_manager/state_update_actions/fund_transactions/index.ts'
import { FundSelect } from '../../../../core/components/FundSelect.tsx'

const { Title, Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const LIST_PATH = '/fund-transactions'
// Status label the ft__ engine treats as immutable (matches the RPC guards).
const STATUS_LABEL_BOOKED = 'Booked'

interface FullFundTransactionFormProps {
  recordId?: string | null
  isNew?: boolean
}

interface ItemRow {
  key: string
  id?: string
  acc_item_id: string | null
  description: string
  amount: number | null
}

interface ManualJournalRow {
  key: string
  id?: string
  acc_item_id: string | null
  debit_credit_id: 'dr' | 'cr'
  amount: number | null
  description: string
  fund_commitment_id: string | null
}

let rowKeySeq = 0
const nextRowKey = () => `row-${++rowKeySeq}`

// Transfer of Interest: the local editing state - the commitment plus one
// row per transferee. Terms are decimal fractions (0.02), edited as percents
// through decimalToPercent / percentToDecimal; they are prefilled from the
// transferor and always sent in full (the server COALESCEs null with the
// commitment's current value). amount = the commitment amount the
// transferee takes; a single row defaults to the whole commitment.
interface TransfereeRow {
  key: string
  id?: string
  to_mode: 'existing' | 'new'
  to_investor_id: string | null
  new_account_id: string | null
  new_investor_name: string
  new_investor_legal_name: string
  new_investor_classification_id: number | null
  new_investor_sbic_type_id: number | null
  amount: number | null
  management_fee_rate: number | null
  base_carry_rate: number | null
  base_hurdle_multiple: number | null
  stepped_carry_rate: number | null
  stepped_hurdle_multiple: number | null
  consideration_amount: number | null
  notes: string
}

interface TransferForm {
  fund_commitment_id: string | null
  transferees: TransfereeRow[]
}

type TransferTerms = Pick<
  TransfereeRow,
  'management_fee_rate' | 'base_carry_rate' | 'base_hurdle_multiple' | 'stepped_carry_rate' | 'stepped_hurdle_multiple'
>

const termsFromCandidate = (row: FundTransferCandidateRow | undefined): TransferTerms => ({
  management_fee_rate: row?.fund_commitments__management_fee_rate ?? null,
  base_carry_rate: row?.fund_commitments__base_carry_rate ?? null,
  base_hurdle_multiple: row?.fund_commitments__base_hurdle_multiple ?? null,
  stepped_carry_rate: row?.fund_commitments__stepped_carry_rate ?? null,
  stepped_hurdle_multiple: row?.fund_commitments__stepped_hurdle_multiple ?? null
})

const emptyTransferee = (terms: TransferTerms, amount: number | null): TransfereeRow => ({
  key: nextRowKey(),
  to_mode: 'existing',
  to_investor_id: null,
  new_account_id: null,
  new_investor_name: '',
  new_investor_legal_name: '',
  new_investor_classification_id: null,
  new_investor_sbic_type_id: null,
  amount,
  ...terms,
  consideration_amount: null,
  notes: ''
})

const EMPTY_TRANSFER_FORM: TransferForm = { fund_commitment_id: null, transferees: [] }

// what Post will do with this transferee set (mirrors ft__transaction_detail
// prog__mode): one transferee taking the whole commitment re-points it,
// anything else splits it
const transferModeOf = (rows: TransfereeRow[], sourceAmount: number | null | undefined): 'whole' | 'split' | null => {
  if (rows.length === 0) return null
  if (rows.length === 1 && sourceAmount != null && (rows[0].amount == null || rows[0].amount >= sourceAmount)) return 'whole'
  return 'split'
}

const ACCOUNT_SEARCH_DEBOUNCE_MS = 300
const PREVIEW_DEBOUNCE_MS = 300

const pctText = (v: number | null | undefined) => (v == null ? '-' : `${decimalToPercent(v)}%`)
const multText = (v: number | null | undefined) => (v == null ? '-' : `${v}x`)

// Quarter-aligned fee-period entry: fee bookings are self-truing (calculated
// = due through the period end minus already booked), so the normal gesture
// is picking ONE quarter - the window becomes that quarter and the start
// date only defines the prior/current display split. The Custom dates
// toggle pre-fills the raw pickers with the quarter's boundaries for
// amending. Values stay plain dates either way - only the entry changes.
const QUARTER_VALUE = (year: number, q: number) => `${year}-Q${q}`
const quarterStartDate = (year: number, q: number) => dayjs(new Date(year, (q - 1) * 3, 1))
const quarterEndDate = (year: number, q: number) =>
  quarterStartDate(year, q).add(3, 'month').subtract(1, 'day')

// The quarter a date represents, if it sits exactly on the relevant
// boundary (quarter start for 'start', quarter end for 'end'); null = the
// date is off-boundary and only the custom pickers can express it.
const quarterOfBoundary = (dateStr: string | null, kind: 'start' | 'end'): string | null => {
  if (!dateStr) return null
  const d = dayjs(dateStr)
  const q = Math.floor(d.month() / 3) + 1
  const boundary = kind === 'start' ? quarterStartDate(d.year(), q) : quarterEndDate(d.year(), q)
  return d.isSame(boundary, 'day') ? QUARTER_VALUE(d.year(), q) : null
}

// The single quarter a window represents, if it spans exactly one calendar
// quarter; null = only the custom pickers can express it.
const singleQuarterOf = (start: string | null, end: string | null): string | null => {
  const s = quarterOfBoundary(start, 'start')
  const e = quarterOfBoundary(end, 'end')
  return s && s === e ? s : null
}

const buildQuarterOptions = () => {
  const thisYear = dayjs().year()
  const options: Array<{ value: string; label: string }> = []
  for (let year = thisYear + 1; year >= thisYear - 10; year--) {
    for (let q = 4; q >= 1; q--) {
      options.push({ value: QUARTER_VALUE(year, q), label: `Q${q} ${year}` })
    }
  }
  return options
}

export default function FullFundTransactionForm({
  recordId: propRecordId,
  isNew: propIsNew
}: FullFundTransactionFormProps) {
  const { urlRecordId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { closeFormTab, openFormInTab } = useTabManagerContext()
  // Modal.useModal, not the static Modal.confirm: under React 19 the static
  // function is CALLED but renders nothing (antd v5 supports React 16-18 and
  // warns "Static function can not consume context"). With the static call the
  // Delete button did nothing at all - no dialog, no delete, no error.
  const [modal, modalContextHolder] = Modal.useModal()

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId ?? null
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new' || !recordId
  // New-record tabs are keyed per prefill query (tabSlice.addFormTab), so the
  // key must carry the mount-time search string. Snapshot it: the global
  // location later reflects whichever tab is active, not this one.
  const [mountSearch] = useState(() => (isNew ? location.search : ''))
  const formTabKey = `fund-transactions-form-${isNew ? 'new' : 'edit'}-${recordId || 'new'}${mountSearch}`

  // Prefill for new records, read once from the query string (set by
  // openFormInTab callers: the Investors tab "Close Commitments" button, the
  // Fund Transactions "+ New" menu).
  const [prefill] = useState<{ fund: string | null; type: number | null; commitment: string | null }>(() => {
    if (!isNew) return { fund: null, type: null, commitment: null }
    const params = new URLSearchParams(location.search)
    const typeParam = Number(params.get('type'))
    return {
      fund: params.get('fund'),
      type: Number.isFinite(typeParam) && typeParam > 0 ? typeParam : null,
      commitment: params.get('commitment')
    }
  })

  const transactionTypes = useSelector((state: any) => state.globalConfigs?.fund_transaction_types ?? [])
  const accItems = useSelector((state: any) => state.globalConfigs?.acc_items ?? [])
  const currencyFmt = useNumberFormat('currency')
  const currencyText = useNumberDisplay('currency')
  const quarterOptions = useMemo(() => buildQuarterOptions(), [])

  const [detail, setDetail] = useState<FundTransactionDetail | null>(null)
  const [loading, setLoading] = useState(!isNew)
  const [notFound, setNotFound] = useState(false)
  const [saving, setSaving] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [activeTab, setActiveTab] = useState('details')

  // Header fields
  const [fundId, setFundId] = useState<string | null>(prefill.fund)
  const [typeId, setTypeId] = useState<number | null>(prefill.type)
  const [transactionDate, setTransactionDate] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  // Management Fees only: with both set, journals book per commitment from
  // the fee engine (spec A3.4)
  const [feePeriodStart, setFeePeriodStart] = useState<string | null>(null)
  const [feePeriodEnd, setFeePeriodEnd] = useState<string | null>(null)
  // false = quarter selects (default); true = raw date pickers. Auto-flips
  // on when a loaded record's window is off quarter boundaries.
  const [feePeriodCustom, setFeePeriodCustom] = useState(false)
  // required-field flags set on a failed save attempt; each clears as soon
  // as its field gets a value (inline red text instead of a transient toast)
  const [fieldErrors, setFieldErrors] = useState<{
    fund?: boolean
    type?: boolean
    date?: boolean
  }>({})

  // Line items + manual journal lines (local editing state)
  const [items, setItems] = useState<ItemRow[]>([])
  const [manualJournals, setManualJournals] = useState<ManualJournalRow[]>([])
  // per-LP fee overrides (A3.5): keyed by fund_commitment_id, null = cleared
  const [feeOverrides, setFeeOverrides] = useState<Record<string, number | null>>({})
  // fee capital tracking (A3.8): fund-level amount per non-remainder funding
  // source, keyed by fee_funding_source_id (the remainder takes the rest)
  const [feeFunding, setFeeFunding] = useState<Record<string, number | null>>({})
  const [lpOptions, setLpOptions] = useState<Array<{ value: string; label: string }>>([])
  // Closing types: the fund's commitments + the ticked set (persists on Save)
  const [closingCandidates, setClosingCandidates] = useState<FundClosingCandidateRow[]>([])
  const [closingLoading, setClosingLoading] = useState(false)
  const [closingError, setClosingError] = useState<string | null>(null)
  const [closingSelection, setClosingSelection] = useState<string[]>([])
  // Transfer types: the fund's commitments, the link being edited and the
  // position preview as at the transaction date
  const [transferCandidates, setTransferCandidates] = useState<FundTransferCandidateRow[]>([])
  const [transferLoading, setTransferLoading] = useState(false)
  const [transferError, setTransferError] = useState<string | null>(null)
  const [transferForm, setTransferForm] = useState<TransferForm>(EMPTY_TRANSFER_FORM)
  const [transferPreview, setTransferPreview] = useState<FundTransferPreview | null>(null)
  const [transferPreviewError, setTransferPreviewError] = useState<string | null>(null)
  const [accountOptions, setAccountOptions] = useState<Array<{ value: string; label: string }>>([])
  const [accountSearching, setAccountSearching] = useState(false)
  const accountSearchSeq = React.useRef(0)
  const accountSearchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const isBooked = detail?.transaction?.status_label === STATUS_LABEL_BOOKED
  const readOnly = isBooked

  // Orchestrated transactions cannot be unbooked here - ft__unbook_transaction
  // refuses them, because regenerating their journals through the generic path
  // would drop the LP-subledger dimensions. Each origin has its own door
  // instead of a button that always throws: a receipt is edited in place, a
  // capital call is reverted on the call itself.
  const contributionId = detail?.transaction?.capital_contribution_id ?? null
  const orchestratedCallId = detail?.transaction?.capital_call_id ?? null
  // a distribution's transaction (phase 3): reverted on the distribution
  const orchestratedDistributionId = detail?.transaction?.distribution_id ?? null
  const [editingContributionId, setEditingContributionId] = useState<string | null>(null)

  const selectedType = useMemo(
    () => transactionTypes.find((t: any) => t.id === typeId) ?? null,
    [transactionTypes, typeId]
  )
  const journalsOnSeparateTab = Boolean(selectedType?.journals_separate_tab)
  // cfg-flag driven (cash + accrual variants); label fallback covers a
  // backend that predates the flag
  const isFeeType = Boolean(selectedType?.books_fee_period) || selectedType?.label === 'Management Fees'
  // with a fee period the engine owns the line item (accounting item and
  // amount); only the saved item's description - the text its journals
  // carry - is the user's, and it survives recompute
  const feeItemManaged = Boolean(isFeeType && feePeriodStart && feePeriodEnd)
  // fee-paying transactions (A3.8): the fee types above plus an Accrued Fees
  // Payment, which the detail marks by carrying a Funding section - so a
  // per-LP payment shows its allocations and overrides like a fee booking
  const paysFees = isFeeType || detail?.fee_funding != null
  const feeFundingInfo = detail?.fee_funding ?? null
  const tracksFeeCapital = Boolean(feeFundingInfo?.applies)
  // a primary fund's fee allocations carry the § 11.9(b) cross-credit: show
  // it as a column so each row adds up to its Calculated amount
  const hasFeeCrossCredit = (detail?.fee_allocations ?? []).some(
    (r) => Math.abs(r.prog__cross_credit_to_date ?? 0) >= 0.005
  )
  // a fund on 'Regulatory capital + assumed leverage' pays the fee on assumed
  // leverage from leverage capital, outside fee capital: show it as a column
  // so Effective - Fee on leverage = Covered by fee capital + From
  // partnership assets
  const hasLeverageFee =
    tracksFeeCapital &&
    (detail?.fee_allocations ?? []).some((r) => Math.abs(r.prog__leverage_fee ?? 0) >= 0.005)
  // cfg-flag driven: Post closes the ticked commitments instead of posting
  // journals (no line items, no journal preview)
  const isClosingType = Boolean(selectedType?.closes_commitments)
  // cfg-flag driven: Post re-points the linked commitment to the transferee
  // instead of posting journals (no line items, no journal preview)
  const isTransferType = Boolean(selectedType?.transfers_interest)
  // transferee pickers (investors of any account + classifications); only
  // fetched for transfer types
  const { options: editorOptions } = useFundCommitmentEditorOptions(isTransferType ? fundId : null)

  // Orchestrated types (Capital Call, Capital Call Receipt) are created by
  // the Capital Calls screen only (spec A2.1); an existing orchestrated
  // transaction still renders read-only here with its label, but the type
  // cannot be chosen for manual booking. Server re-checks.
  const typeOptions = useMemo(
    () =>
      transactionTypes
        .filter((t: any) => !t.orchestrated || t.id === typeId)
        .map((t: any) => ({ value: t.id, label: t.label })),
    [transactionTypes, typeId]
  )
  const accItemOptions = useMemo(
    () => accItems.map((i: any) => ({ value: i.id, label: i.label })),
    [accItems]
  )

  const hydrateFromDetail = useCallback((d: FundTransactionDetail) => {
    setDetail(d)
    setFundId(d.transaction.fund_id)
    setTypeId(d.transaction.transaction_type_id)
    setTransactionDate(d.transaction.transaction_date)
    setDescription(d.transaction.description ?? '')
    setFeePeriodStart(d.transaction.fee_period_start ?? null)
    setFeePeriodEnd(d.transaction.fee_period_end ?? null)
    setFeePeriodCustom(
      Boolean(
        (d.transaction.fee_period_start || d.transaction.fee_period_end) &&
          !singleQuarterOf(
            d.transaction.fee_period_start ?? null,
            d.transaction.fee_period_end ?? null
          )
      )
    )
    setItems(
      d.items
        // fee-capital funding mirrors are engine-managed (A3.8), not user lines
        .filter((i) => !i.fee_funding_source_id)
        .map((i) => ({
          key: i.id,
          id: i.id,
          acc_item_id: i.acc_item_id,
          description: i.description ?? '',
          amount: i.amount
        }))
    )
    setManualJournals(
      d.journals
        .filter((j) => j.is_manual)
        .map((j) => ({
          key: j.id,
          id: j.id,
          acc_item_id: j.item_id ?? null,
          debit_credit_id: (j.debit_credit_id === 'cr' ? 'cr' : 'dr') as 'dr' | 'cr',
          amount: j.amount,
          description: j.description ?? '',
          fund_commitment_id: j.fund_commitment_id ?? null
        }))
    )
    setFeeOverrides(
      Object.fromEntries(
        (d.fee_allocations ?? []).map((a) => [a.fund_commitment_id, a.override_amount ?? null])
      )
    )
    setFeeFunding(
      Object.fromEntries(
        (d.fee_funding?.sources ?? [])
          .filter((s) => !s.is_remainder)
          .map((s) => [s.id, s.amount_input ?? null])
      )
    )
    const x = d.transfer
    setTransferForm(
      x
        ? {
            fund_commitment_id: x.fund_commitment_id,
            transferees: (x.transferees ?? []).map((t) => ({
              key: t.id,
              id: t.id,
              to_mode: 'existing' as const,
              to_investor_id: t.to_investor_id ?? null,
              new_account_id: null,
              new_investor_name: '',
              new_investor_legal_name: '',
              new_investor_classification_id: null,
              new_investor_sbic_type_id: null,
              amount: t.amount ?? null,
              management_fee_rate: t.effective_management_fee_rate ?? null,
              base_carry_rate: t.effective_base_carry_rate ?? null,
              base_hurdle_multiple: t.effective_base_hurdle_multiple ?? null,
              stepped_carry_rate: t.effective_stepped_carry_rate ?? null,
              stepped_hurdle_multiple: t.effective_stepped_hurdle_multiple ?? null,
              consideration_amount: t.consideration_amount ?? null,
              notes: t.notes ?? ''
            }))
          }
        : EMPTY_TRANSFER_FORM
    )
    setDirty(false)
  }, [])

  // Cold-load detail (screen-local; the hard-refresh path is the normal path).
  useEffect(() => {
    let cancelled = false
    if (isNew) {
      const prefilledType = prefill.type ? transactionTypes.find((t: any) => t.id === prefill.type) : null
      dispatch(
        updateTabTitle({
          key: formTabKey,
          title: prefilledType?.closes_commitments
            ? 'New Closing'
            : prefilledType?.transfers_interest
              ? 'New Transfer of Interest'
              : 'New Fund Transaction'
        })
      )
      setItems([{ key: nextRowKey(), acc_item_id: null, description: '', amount: null }])
      return
    }
    ;(async () => {
      setLoading(true)
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundTransactionDetail(String(recordId), token)
      if (cancelled) return
      if (res.error || !res.data) {
        if (res.error) showErrorToast(res.error)
        setNotFound(true)
        setLoading(false)
        return
      }
      hydrateFromDetail(res.data)
      dispatch(
        updateTabTitle({
          key: formTabKey,
          title: `${res.data.transaction.transaction_type_label ?? 'Transaction'} — ${res.data.transaction.fund_name ?? ''}`
        })
      )
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [recordId, isNew, dispatch, formTabKey, hydrateFromDetail, prefill.type, transactionTypes])

  const markDirty = () => setDirty(true)

  // Closing types: load the fund's commitments with their selection state
  // for this transaction. Re-runs when the fund or the status changes (Post
  // stamps dates, Unbook clears them); a plain Save keeps the local ticks.
  const detailId = detail?.transaction?.id ?? null
  const detailStatusLabel = detail?.transaction?.status_label ?? null
  useEffect(() => {
    if (!isClosingType || !fundId) {
      setClosingCandidates([])
      setClosingSelection([])
      setClosingError(null)
      return
    }
    let cancelled = false
    setClosingLoading(true)
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundClosingCandidates(fundId, detailId, token)
      if (cancelled) return
      setClosingLoading(false)
      if (res.error || !res.data) {
        setClosingError(res.error ?? 'Failed to load commitments')
        setClosingCandidates([])
        setClosingSelection([])
        return
      }
      setClosingError(null)
      setClosingCandidates(res.data.rows)
      setClosingSelection(res.data.rows.filter((r) => r.prog__selected).map((r) => r.fund_commitments__id))
    })()
    return () => {
      cancelled = true
    }
  }, [isClosingType, fundId, detailId, detailStatusLabel])

  // Transfer types: load the fund's commitments with their selection state
  // for this transaction. A new record preselects ?commitment= and takes
  // its terms; a loaded record keeps what hydrateFromDetail seeded.
  useEffect(() => {
    if (!isTransferType || !fundId) {
      setTransferCandidates([])
      setTransferError(null)
      return
    }
    let cancelled = false
    setTransferLoading(true)
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundTransferCandidates(fundId, detailId, token)
      if (cancelled) return
      setTransferLoading(false)
      if (res.error || !res.data) {
        setTransferError(res.error ?? 'Failed to load commitments')
        setTransferCandidates([])
        return
      }
      setTransferError(null)
      setTransferCandidates(res.data.rows)
      setTransferForm((prev) => {
        if (prev.fund_commitment_id || !prefill.commitment) return prev
        const row = res.data!.rows.find((r) => r.fund_commitments__id === prefill.commitment)
        return row
          ? {
              fund_commitment_id: row.fund_commitments__id,
              transferees: [emptyTransferee(termsFromCandidate(row), row.fund_commitments__amount ?? null)]
            }
          : prev
      })
    })()
    return () => {
      cancelled = true
    }
  }, [isTransferType, fundId, detailId, detailStatusLabel, prefill.commitment])

  // Position preview as at the transaction date while Draft (debounced on
  // commitment + date); a Booked transfer shows the stamped snapshot instead.
  const transferCommitmentId = transferForm.fund_commitment_id
  const bookedSnapshot = useMemo(() => {
    const raw = detail?.transfer?.position_at_transfer
    if (!raw) return null
    const parsed = FundTransferPreviewSchema.safeParse(raw)
    return parsed.success ? parsed.data : null
  }, [detail])
  useEffect(() => {
    if (!isTransferType || !fundId || !transferCommitmentId || !transactionDate || isBooked) {
      setTransferPreview(null)
      setTransferPreviewError(null)
      return
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundTransferPreview(fundId, transferCommitmentId, transactionDate, token)
      if (cancelled) return
      if (res.error || !res.data) {
        setTransferPreview(null)
        setTransferPreviewError(res.error ?? 'Failed to load the position')
        return
      }
      setTransferPreviewError(null)
      setTransferPreview(res.data)
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [isTransferType, fundId, transferCommitmentId, transactionDate, isBooked])

  // Debounced account search for a new transferee entity
  // (the former commitment pop-up's pattern); the sequence number drops a stale
  // response that lands after a newer one.
  const searchAccounts = useCallback((term: string) => {
    const seq = ++accountSearchSeq.current
    if (accountSearchTimer.current) clearTimeout(accountSearchTimer.current)
    if (!term.trim()) {
      setAccountOptions([])
      setAccountSearching(false)
      return
    }
    setAccountSearching(true)
    accountSearchTimer.current = setTimeout(async () => {
      try {
        const res = await searchCompaniesForPopup(term, 20, getAccessToken(store.getState().auth))
        if (seq === accountSearchSeq.current) {
          setAccountOptions((res.data ?? []).map((a) => ({ value: String(a.value), label: String(a.label) })))
        }
      } finally {
        if (seq === accountSearchSeq.current) setAccountSearching(false)
      }
    }, ACCOUNT_SEARCH_DEBOUNCE_MS)
  }, [])
  useEffect(
    () => () => {
      if (accountSearchTimer.current) clearTimeout(accountSearchTimer.current)
    },
    []
  )

  // choosing the commitment resets the transferee list to one row taking
  // the whole commitment with the transferor's terms
  const chooseTransferCommitment = (fundCommitmentId: string) => {
    const row = transferCandidates.find((r) => r.fund_commitments__id === fundCommitmentId)
    setTransferForm({
      fund_commitment_id: fundCommitmentId,
      transferees: [emptyTransferee(termsFromCandidate(row), row?.fund_commitments__amount ?? null)]
    })
    markDirty()
  }
  const updateTransferee = (key: string, patch: Partial<TransfereeRow>) => {
    setTransferForm((prev) => ({
      ...prev,
      transferees: prev.transferees.map((t) => (t.key === key ? { ...t, ...patch } : t))
    }))
    markDirty()
  }
  const addTransferee = () => {
    setTransferForm((prev) => {
      const row = transferCandidates.find((r) => r.fund_commitments__id === prev.fund_commitment_id)
      const sourceAmount = row?.fund_commitments__amount ?? null
      const taken = prev.transferees.reduce((sum, t) => sum + (t.amount ?? 0), 0)
      const remaining = sourceAmount == null ? null : Math.max(0, sourceAmount - taken)
      return {
        ...prev,
        transferees: [...prev.transferees, emptyTransferee(termsFromCandidate(row), remaining && remaining > 0 ? remaining : null)]
      }
    })
    markDirty()
  }
  const removeTransferee = (key: string) => {
    setTransferForm((prev) => ({ ...prev, transferees: prev.transferees.filter((t) => t.key !== key) }))
    markDirty()
  }
  // the commitment being assigned (candidate row)
  const transferRow = useMemo(
    () => transferCandidates.find((r) => r.fund_commitments__id === transferForm.fund_commitment_id),
    [transferCandidates, transferForm.fund_commitment_id]
  )

  // ----- item grid handlers -----
  const updateItem = (key: string, patch: Partial<ItemRow>) => {
    setItems((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
    markDirty()
  }
  const addItem = () => {
    setItems((prev) => [...prev, { key: nextRowKey(), acc_item_id: null, description: '', amount: null }])
    markDirty()
  }
  const removeItem = (key: string) => {
    setItems((prev) => prev.filter((r) => r.key !== key))
    markDirty()
  }

  // ----- manual journal handlers -----
  const updateManualJournal = (key: string, patch: Partial<ManualJournalRow>) => {
    setManualJournals((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
    markDirty()
  }
  const addManualJournal = () => {
    setManualJournals((prev) => [
      ...prev,
      { key: nextRowKey(), acc_item_id: null, debit_credit_id: 'dr', amount: null, description: '', fund_commitment_id: null }
    ])
    markDirty()
  }
  const removeManualJournal = (key: string) => {
    setManualJournals((prev) => prev.filter((r) => r.key !== key))
    markDirty()
  }

  // ----- save / post / unbook / delete -----
  const handleSave = useCallback(async () => {
    if (!fundId || !typeId || !transactionDate) {
      setFieldErrors({
        fund: !fundId,
        type: !typeId,
        date: !transactionDate
      })
      return
    }
    setFieldErrors({})
    // closing types carry no items / journals: the default empty item row
    // must not block Create
    const noLines = isClosingType || isTransferType
    const invalidItem =
      feeItemManaged || noLines
        ? undefined
        : items.find((i) => !i.acc_item_id || i.amount == null || i.amount === 0)
    if (invalidItem) {
      showErrorToast('Every line item needs an accounting item and a non-zero amount')
      return
    }
    const invalidJournal = noLines
      ? undefined
      : manualJournals.find((j) => !j.acc_item_id || j.amount == null || j.amount <= 0)
    if (invalidJournal) {
      showErrorToast('Every manual journal line needs an accounting item and a positive amount')
      return
    }
    if (isTransferType) {
      if (!transferForm.fund_commitment_id) {
        showErrorToast('Choose the commitment to transfer')
        return
      }
      if (transferForm.transferees.length === 0) {
        showErrorToast('Add at least one transferee')
        return
      }
      for (const t of transferForm.transferees) {
        if (t.to_mode === 'existing' && !t.to_investor_id) {
          showErrorToast('Choose the transferee on every row')
          return
        }
        if (t.to_mode === 'new' && (!t.new_account_id || !t.new_investor_name.trim())) {
          showErrorToast('A new investor needs an account and a name')
          return
        }
        if (t.amount == null || t.amount <= 0) {
          showErrorToast('Every transferee needs the amount it takes')
          return
        }
      }
      const sourceAmount = transferRow?.fund_commitments__amount ?? null
      const taken = transferForm.transferees.reduce((sum, t) => sum + (t.amount ?? 0), 0)
      if (sourceAmount != null && taken > sourceAmount + 0.005) {
        showErrorToast(`The transferees take ${currencyText(taken)} - more than the ${currencyText(sourceAmount)} commitment`)
        return
      }
      const seen = new Set<string>()
      for (const t of transferForm.transferees) {
        if (t.to_mode === 'existing' && t.to_investor_id) {
          if (seen.has(t.to_investor_id)) {
            showErrorToast('The same investor is listed twice as a transferee')
            return
          }
          seen.add(t.to_investor_id)
        }
      }
    }

    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const payload: FundTransactionSavePayload = {
        ...(detail?.transaction?.id ? { id: detail.transaction.id } : {}),
        ...(detail?.transaction?.id ? {} : { fund_id: fundId }),
        transaction_type_id: typeId,
        transaction_date: transactionDate,
        description: description || null,
        fee_period_start: isFeeType ? feePeriodStart : null,
        fee_period_end: isFeeType ? feePeriodEnd : null,
        // a fee period sends only the saved engine item, for its description
        items: (noLines ? [] : feeItemManaged ? items.filter((i) => i.id) : items).map((i, idx) => ({
          ...(i.id ? { id: i.id } : {}),
          acc_item_id: i.acc_item_id as string,
          description: i.description || null,
          amount: i.amount as number,
          display_order: idx + 1
        })),
        manual_journals: (noLines ? [] : manualJournals).map((j) => ({
          ...(j.id ? { id: j.id } : {}),
          acc_item_id: j.acc_item_id as string,
          debit_credit_id: j.debit_credit_id,
          amount: j.amount as number,
          description: j.description || null,
          fund_commitment_id: j.fund_commitment_id
        })),
        ...(isClosingType ? { commitment_ids: closingSelection } : {}),
        ...(isTransferType && transferForm.fund_commitment_id
          ? {
              transfer: {
                fund_commitment_id: transferForm.fund_commitment_id,
                transferees: transferForm.transferees.map((t) => ({
                  ...(t.id ? { id: t.id } : {}),
                  ...(t.to_mode === 'existing'
                    ? { to_investor_id: t.to_investor_id }
                    : {
                        new_investor: {
                          account_id: t.new_account_id as string,
                          name: t.new_investor_name.trim(),
                          legal_name: t.new_investor_legal_name.trim() || null,
                          investor_classification_id: t.new_investor_classification_id,
                          sbic_investor_type_id: t.new_investor_sbic_type_id
                        }
                      }),
                  amount: t.amount as number,
                  management_fee_rate: t.management_fee_rate,
                  base_carry_rate: t.base_carry_rate,
                  base_hurdle_multiple: t.base_hurdle_multiple,
                  stepped_carry_rate: t.stepped_carry_rate,
                  stepped_hurdle_multiple: t.stepped_hurdle_multiple,
                  consideration_amount: t.consideration_amount,
                  notes: t.notes.trim() || null
                }))
              }
            }
          : {}),
        ...(paysFees && Object.keys(feeOverrides).length > 0
          ? {
              fee_overrides: Object.entries(feeOverrides).map(([fcId, override]) => ({
                fund_commitment_id: fcId,
                override_amount: override
              }))
            }
          : {}),
        // fee capital tracking (A3.8): the amounts entered per non-remainder
        // source; blank = 0 (the remainder source takes the rest)
        ...(paysFees && Object.keys(feeFunding).length > 0
          ? {
              fee_funding: Object.entries(feeFunding).map(([sourceId, amount]) => ({
                fee_funding_source_id: sourceId,
                amount: amount ?? 0
              }))
            }
          : {})
      }

      const res = await saveFundTransaction(payload, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to save fund transaction')
        return
      }

      const wasNew = !detail?.transaction?.id
      hydrateFromDetail(res.data)
      if (res.data.generation?.status === 'skipped' && res.data.generation?.message) {
        toast.info(res.data.generation.message)
      }
      toast.success(
        isClosingType
          ? wasNew
            ? 'Closing created'
            : 'Closing saved'
          : isTransferType
            ? wasNew
              ? 'Transfer created'
              : 'Transfer saved'
            : wasNew
              ? 'Fund transaction created'
              : 'Fund transaction saved'
      )

      if (wasNew) {
        // Morph the "new" tab into the edit tab in place (no remove+re-add:
        // that left the opener tab visible for one paint — screen flash).
        dispatch(
          transformFormTab({
            key: formTabKey,
            module: TABLE_FUND_TRANSACTIONS,
            recordId: res.data.transaction.id,
            title: `${res.data.transaction.transaction_type_label ?? 'Transaction'} — ${res.data.transaction.fund_name ?? ''}`
          })
        )
        navigate(`/${TABLE_FUND_TRANSACTIONS}/forms/full/${res.data.transaction.id}`, { replace: true })
      }
    } finally {
      setSaving(false)
    }
  }, [
    fundId,
    typeId,
    transactionDate,
    description,
    isFeeType,
    feeItemManaged,
    isClosingType,
    isTransferType,
    transferForm,
    transferRow,
    currencyText,
    closingSelection,
    feePeriodStart,
    feePeriodEnd,
    items,
    manualJournals,
    feeOverrides,
    feeFunding,
    paysFees,
    detail,
    dispatch,
    formTabKey,
    hydrateFromDetail,
    navigate
  ])

  // returns the fresh detail so a caller can patch the still-mounted list row
  const refetchDetail = useCallback(async (): Promise<FundTransactionDetail | null> => {
    if (!detail?.transaction?.id) return null
    const token = getAccessToken(store.getState().auth)
    const res = await fetchFundTransactionDetail(detail.transaction.id, token)
    if (res.data) hydrateFromDetail(res.data)
    return res.data ?? null
  }, [detail, hydrateFromDetail])

  const handleBook = useCallback(async () => {
    if (!detail?.transaction?.id) return
    setActionLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await bookFundTransaction(detail.transaction.id, token)
      if (res.error) {
        const diff = res.data?.difference
        showErrorToast(diff != null ? `${res.error} (difference: ${diff})` : res.error)
        return
      }
      toast.success(
        isClosingType
          ? 'Commitments closed'
          : isTransferType
            ? res.data?.prog__mode === 'split'
              ? 'Interest transferred - the commitment was split'
              : 'Interest transferred'
            : 'Transaction posted'
      )
      dispatch(
        updateFundTransactionRecord({
          transactionId: detail.transaction.id,
          updates: { cfg_transaction_statuses__label: STATUS_LABEL_BOOKED }
        })
      )
      await refetchDetail()
    } finally {
      setActionLoading(false)
    }
  }, [detail, dispatch, refetchDetail, isClosingType, isTransferType])

  const handleUnbook = useCallback(async () => {
    if (!detail?.transaction?.id) return
    setActionLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await unbookFundTransaction(detail.transaction.id, token)
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      toast.success(
        isClosingType
          ? 'Closing reverted — commitments reopened'
          : isTransferType
            ? 'Transfer reverted — the prior holder has the whole commitment again'
            : 'Transaction reverted to draft'
      )
      dispatch(
        updateFundTransactionRecord({
          transactionId: detail.transaction.id,
          updates: { cfg_transaction_statuses__label: 'Draft' }
        })
      )
      await refetchDetail()
    } finally {
      setActionLoading(false)
    }
  }, [detail, dispatch, refetchDetail, isClosingType, isTransferType])

  const handleDelete = useCallback(() => {
    if (!detail?.transaction?.id) return
    modal.confirm({
      title: isClosingType
        ? 'Delete this closing?'
        : isTransferType
          ? 'Delete this transfer?'
          : 'Delete this fund transaction?',
      content: isClosingType
        ? 'The draft closing will be moved to trash. No commitment is affected.'
        : isTransferType
          ? 'The draft transfer will be moved to trash. The commitment stays with its current holder.'
          : 'The transaction, its line items and all journal lines will be moved to trash.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const token = getAccessToken(store.getState().auth)
        const res = await trashFundTransaction(detail.transaction.id, token)
        if (res.error) {
          showErrorToast(res.error)
          return
        }
        toast.success(isClosingType ? 'Closing deleted' : isTransferType ? 'Transfer deleted' : 'Fund transaction deleted')
        dispatch(removeFundTransaction({ transactionId: detail.transaction.id }))
        closeFormTab(formTabKey)
      }
    })
  }, [detail, dispatch, formTabKey, closeFormTab, modal, isClosingType, isTransferType])

  // ----- derived: displayed journal set + totals -----
  const autoJournals = useMemo(() => (detail?.journals ?? []).filter((j) => !j.is_manual), [detail])

  // LP options for manual-line Investor stamps + the fee allocation grid
  useEffect(() => {
    if (!fundId) {
      setLpOptions([])
      return
    }
    let cancelled = false
    const token = getAccessToken(store.getState().auth)
    ;(async () => {
      const res = await fetchJournalEntryOptions(fundId, token)
      if (!cancelled) setLpOptions(res.data.lps)
    })()
    return () => {
      cancelled = true
    }
  }, [fundId])

  const totals = useMemo(() => {
    let debits = 0
    let credits = 0
    for (const j of autoJournals) {
      if (j.debit_credit_id === 'dr') debits += j.amount
      else credits += j.amount
    }
    for (const j of manualJournals) {
      const amt = j.amount ?? 0
      if (j.debit_credit_id === 'dr') debits += amt
      else credits += amt
    }
    return { debits, credits, difference: debits - credits }
  }, [autoJournals, manualJournals])

  // float tolerance: client-side summation of many journal lines carries FP
  // epsilon (e.g. 30-line fee transactions); the server's exact numeric
  // balance check in ft__book_transaction stays authoritative
  const isBalanced = Math.abs(totals.difference) < 0.005 && autoJournals.length + manualJournals.length > 0
  // closing: Post needs a SAVED non-empty selection (the server re-checks)
  const savedClosingCount = detail?.closing_commitments?.length ?? 0
  // transfer: Post needs a SAVED transferee set (the server re-checks)
  const savedTransferee = (detail?.transfer?.transferees?.length ?? 0) > 0 || Boolean(detail?.transfer?.to_investor_id)
  const canPost =
    !isNewRecord() &&
    !dirty &&
    !readOnly &&
    (isClosingType ? savedClosingCount > 0 : isTransferType ? savedTransferee : isBalanced)

  function isNewRecord() {
    return !detail?.transaction?.id
  }

  // ----- closing: derived selection state -----
  const closingRows = useMemo(
    () => (readOnly ? closingCandidates.filter((r) => r.prog__selected) : closingCandidates),
    [closingCandidates, readOnly]
  )
  const closingSelectedTotal = useMemo(() => {
    const selected = new Set(closingSelection)
    return closingCandidates.reduce(
      (sum, r) =>
        selected.has(r.fund_commitments__id)
          ? sum + Number(r.prog__amount_at_close ?? r.fund_commitments__amount ?? 0)
          : sum,
      0
    )
  }, [closingCandidates, closingSelection])
  // "open" = not yet Closed at all; legacy Closed-without-date rows are
  // candidates but only join a closing when ticked deliberately
  const openCandidateIds = useMemo(
    () =>
      closingCandidates
        .filter((r) => !r.prog__closed_elsewhere && r.fund_commitment_statuses__label !== 'Closed')
        .map((r) => r.fund_commitments__id),
    [closingCandidates]
  )

  // ----- renderers -----

  const itemColumns = [
    {
      title: 'Accounting Item',
      dataIndex: 'acc_item_id',
      width: 260,
      render: (_: unknown, row: ItemRow) => (
        <Select
          size='small'
          className='w-full'
          value={row.acc_item_id}
          options={accItemOptions}
          placeholder='Select item...'
          showSearch
          optionFilterProp='label'
          disabled={readOnly || feeItemManaged}
          onChange={(v) => updateItem(row.key, { acc_item_id: v })}
        />
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (_: unknown, row: ItemRow) => (
        <Input
          size='small'
          value={row.description}
          placeholder={feeItemManaged && !row.id ? 'Editable after the first save' : undefined}
          disabled={readOnly || (feeItemManaged && !row.id)}
          onChange={(e) => updateItem(row.key, { description: e.target.value })}
        />
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 170,
      align: 'right' as const,
      render: (_: unknown, row: ItemRow) => (
        <InputNumber
          size='small'
          className='w-full'
          value={row.amount}
          {...currencyFmt}
          disabled={readOnly || feeItemManaged}
          onChange={(v) => updateItem(row.key, { amount: v == null ? null : Number(v) })}
        />
      )
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 40,
      render: (_: unknown, row: ItemRow) =>
        readOnly || feeItemManaged ? null : (
          <Button
            type='text'
            size='small'
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeItem(row.key)}
            disabled={items.length <= 1}
          />
        )
    }
  ]

  type JournalDisplayRow = {
    key: string
    kind: 'auto' | 'manual'
    manualRow?: ManualJournalRow
    item_label?: string | null
    description?: string | null
    debit_credit_id: string
    amount: number | null
    investorName?: string | null
  }

  const journalDisplayRows: JournalDisplayRow[] = useMemo(() => {
    const autos: JournalDisplayRow[] = autoJournals.map((j) => ({
      key: j.id,
      kind: 'auto',
      item_label: j.item_label,
      description: j.description,
      debit_credit_id: j.debit_credit_id,
      amount: j.amount,
      investorName: j.investors__name ?? null
    }))
    const manuals: JournalDisplayRow[] = manualJournals.map(
      (j): JournalDisplayRow => ({
        key: j.key,
        kind: 'manual',
        manualRow: j,
        item_label: null,
        description: j.description,
        debit_credit_id: j.debit_credit_id,
        amount: j.amount
      })
    )
    return [...autos, ...manuals]
  }, [autoJournals, manualJournals])

  const journalColumns = [
    {
      title: 'Accounting Item',
      dataIndex: 'item_label',
      width: 260,
      render: (_: unknown, row: JournalDisplayRow) =>
        row.kind === 'auto' ? (
          row.item_label
        ) : (
          <Select
            size='small'
            className='w-full'
            value={row.manualRow!.acc_item_id}
            options={accItemOptions}
            placeholder='Select item...'
            showSearch
            optionFilterProp='label'
            disabled={readOnly}
            onChange={(v) => updateManualJournal(row.key, { acc_item_id: v })}
          />
        )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (_: unknown, row: JournalDisplayRow) =>
        row.kind === 'auto' ? (
          row.description
        ) : (
          <Input
            size='small'
            value={row.manualRow!.description}
            disabled={readOnly}
            onChange={(e) => updateManualJournal(row.key, { description: e.target.value })}
          />
        )
    },
    {
      title: 'Dr / Cr',
      dataIndex: 'debit_credit_id',
      width: 110,
      render: (_: unknown, row: JournalDisplayRow) =>
        row.kind === 'auto' ? (
          row.debit_credit_id === 'dr' ? 'Debit' : 'Credit'
        ) : (
          <Select
            size='small'
            className='w-full'
            value={row.manualRow!.debit_credit_id}
            options={[
              { value: 'dr', label: 'Debit' },
              { value: 'cr', label: 'Credit' }
            ]}
            disabled={readOnly}
            onChange={(v) => updateManualJournal(row.key, { debit_credit_id: v as 'dr' | 'cr' })}
          />
        )
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, row: JournalDisplayRow) => {
        if (row.kind === 'manual' && row.debit_credit_id === 'dr') {
          return (
            <InputNumber
              size='small'
              className='w-full'
              value={row.manualRow!.amount}
              {...currencyFmt}
              min={0.01}
              disabled={readOnly}
              onChange={(v) => updateManualJournal(row.key, { amount: v == null ? null : Number(v) })}
            />
          )
        }
        return row.debit_credit_id === 'dr' && row.amount != null ? currencyText(row.amount) : null
      }
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, row: JournalDisplayRow) => {
        if (row.kind === 'manual' && row.debit_credit_id === 'cr') {
          return (
            <InputNumber
              size='small'
              className='w-full'
              value={row.manualRow!.amount}
              {...currencyFmt}
              min={0.01}
              disabled={readOnly}
              onChange={(v) => updateManualJournal(row.key, { amount: v == null ? null : Number(v) })}
            />
          )
        }
        return row.debit_credit_id === 'cr' && row.amount != null ? currencyText(row.amount) : null
      }
    },
    {
      title: 'Investor',
      dataIndex: 'fund_commitment_id',
      width: 180,
      render: (_: unknown, row: JournalDisplayRow) => {
        if (row.kind === 'manual') {
          return (
            <Select
              size='small'
              className='w-full'
              value={row.manualRow!.fund_commitment_id}
              options={lpOptions}
              placeholder='(fund level)'
              allowClear
              showSearch
              optionFilterProp='label'
              disabled={readOnly}
              onChange={(v) => updateManualJournal(row.key, { fund_commitment_id: v ?? null })}
            />
          )
        }
        return row.investorName ? <Text type='secondary'>{row.investorName}</Text> : null
      }
    },
    {
      title: 'Source',
      dataIndex: 'kind',
      width: 90,
      render: (_: unknown, row: JournalDisplayRow) =>
        row.kind === 'manual' ? <Tag color='orange'>Manual</Tag> : <Tag>Auto</Tag>
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 40,
      render: (_: unknown, row: JournalDisplayRow) =>
        row.kind === 'manual' && !readOnly ? (
          <Button
            type='text'
            size='small'
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeManualJournal(row.key)}
          />
        ) : null
    }
  ]

  // shown on the derived cards while unsaved edits exist: the amounts on
  // screen are the last-saved state until Save regenerates them
  const staleTag =
    dirty && !isNewRecord() ? (
      <Tag color='orange' className='m-0 font-normal'>
        Outdated — save to recalculate
      </Tag>
    ) : null

  const journalPreview = (
    <FormSection
      title='Journal Preview'
      titleColor='green'
      scrollable={false}
      headerActions={
        <>
          {staleTag}
          {readOnly ? null : (
            <Button size='small' icon={<PlusOutlined />} onClick={addManualJournal} disabled={isNewRecord()}>
              Add Journal Line
            </Button>
          )}
        </>
      }
    >
      {isNewRecord() ? (
        <Text type='secondary'>Save the transaction to generate the journal preview.</Text>
      ) : (
        <>
          {dirty ? (
            <Alert
              className='mb-2'
              type='info'
              showIcon
              message='Unsaved changes — the auto journal lines refresh when you save.'
            />
          ) : null}
          <Table
            size='small'
            columns={journalColumns}
            dataSource={journalDisplayRows}
            pagination={false}
            rowKey='key'
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row className='font-medium'>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    Totals
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align='right'>
                    {currencyText(totals.debits)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align='right'>
                    {currencyText(totals.credits)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} colSpan={2}>
                    {isBalanced ? (
                      <Tag color='green'>Balanced</Tag>
                    ) : (
                      <Tag color='red'>Difference: {currencyText(totals.difference)}</Tag>
                    )}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </>
      )}
    </FormSection>
  )

  const closingColumns = [
    {
      title: 'Investor',
      dataIndex: 'investors__name',
      render: (_: unknown, row: FundClosingCandidateRow) => (
        <div>
          <div>{row.investors__name ?? '-'}</div>
          {row.accounts__name && row.accounts__name !== row.investors__name ? (
            <Text type='secondary' className='text-xs'>
              {row.accounts__name}
            </Text>
          ) : null}
        </div>
      )
    },
    {
      title: readOnly ? 'Amount at close' : 'Commitment',
      dataIndex: 'fund_commitments__amount',
      width: 160,
      align: 'right' as const,
      render: (_: unknown, row: FundClosingCandidateRow) =>
        currencyText(Number(row.prog__amount_at_close ?? row.fund_commitments__amount ?? 0))
    },
    {
      title: 'Status',
      dataIndex: 'fund_commitment_statuses__label',
      width: 120,
      render: (v: string | null) =>
        v ? <Tag color={v === 'Closed' ? 'green' : 'blue'}>{v}</Tag> : <Text type='secondary'>-</Text>
    },
    {
      title: 'Committed',
      dataIndex: 'fund_commitments__date_committed',
      width: 120,
      render: (v: string | null) => (v ? dayjs(v).format(DATE_FORMAT) : <Text type='secondary'>-</Text>)
    },
    {
      title: 'Closed',
      dataIndex: 'fund_commitments__date_closed',
      width: 200,
      render: (v: string | null, row: FundClosingCandidateRow) => {
        if (row.prog__closed_elsewhere) {
          return (
            <Text type='secondary'>
              {v ? dayjs(v).format(DATE_FORMAT) : '-'}
              {row.prog__closed_by_label ? ` · ${row.prog__closed_by_label}` : ''}
            </Text>
          )
        }
        if (v) return dayjs(v).format(DATE_FORMAT)
        return row.fund_commitment_statuses__label === 'Closed' ? (
          <Tooltip title='Closed with no date on record — tick it to stamp this closing date'>
            <Tag>no date</Tag>
          </Tooltip>
        ) : (
          <Text type='secondary'>-</Text>
        )
      }
    }
  ]

  const commitmentsSection = (
    <FormSection
      title='Commitments'
      titleColor='orange'
      scrollable={false}
      headerActions={
        readOnly ? (
          <Text type='secondary'>
            {closingRows.length} closed · {currencyText(closingSelectedTotal)}
          </Text>
        ) : (
          <>
            <Text type='secondary'>
              {closingSelection.length} of {closingCandidates.length} selected · {currencyText(closingSelectedTotal)}
            </Text>
            <Button
              size='small'
              disabled={closingLoading || openCandidateIds.length === 0}
              onClick={() => {
                setClosingSelection((prev) => Array.from(new Set([...prev, ...openCandidateIds])))
                markDirty()
              }}
            >
              Select all open
            </Button>
            <Button
              size='small'
              disabled={closingSelection.length === 0}
              onClick={() => {
                setClosingSelection([])
                markDirty()
              }}
            >
              Clear
            </Button>
          </>
        )
      }
    >
      {!fundId ? (
        <Text type='secondary'>Select a fund to list its commitments.</Text>
      ) : closingError ? (
        <Alert type='error' showIcon message={closingError} />
      ) : (
        <Table
          size='small'
          loading={closingLoading}
          columns={closingColumns}
          dataSource={closingRows}
          pagination={false}
          rowKey='fund_commitments__id'
          rowClassName={(row: FundClosingCandidateRow) => (row.prog__closed_elsewhere ? 'opacity-50' : '')}
          rowSelection={
            readOnly
              ? undefined
              : {
                  selectedRowKeys: closingSelection,
                  onChange: (keys) => {
                    setClosingSelection(keys.map(String))
                    markDirty()
                  },
                  getCheckboxProps: (row: FundClosingCandidateRow) => ({
                    disabled: row.prog__closed_elsewhere
                  })
                }
          }
          locale={{ emptyText: readOnly ? 'No commitments were closed.' : 'This fund has no commitments to close.' }}
        />
      )}
      {!readOnly ? (
        <Text type='secondary' className='block mt-2'>
          Post sets every ticked commitment to Closed as of the closing date. Rows already closed with a
          date are managed by that closing; a Closed row with no date is a legacy record — tick it to stamp
          this date. Save keeps the selection.
        </Text>
      ) : null}
    </FormSection>
  )

  // ----- transfer: derived state + section -----
  const transferCandidateOptions = useMemo(
    () =>
      transferCandidates.map((r) => {
        const reason = r.prog__transferred_elsewhere
          ? `on transfer "${r.prog__transferred_by_label ?? ''}"`
          : r.prog__not_live
            ? r.fund_commitment_statuses__label?.toLowerCase()
            : null
        // two commitments of one investor: the label already carries the
        // amount and closing date that tell them apart
        const name =
          r.prog__commitment_label && r.prog__commitment_label !== r.investors__name
            ? r.prog__commitment_label
            : `${r.investors__name ?? ''} · ${r.fund_commitments__amount_fmt ?? ''}`
        return {
          value: r.fund_commitments__id,
          label: `${name}${reason ? ` (${reason})` : ''}`,
          disabled: Boolean(reason)
        }
      }),
    [transferCandidates]
  )
  // the holder cannot be a transferee; an investor already on another row
  // is offered once
  const transfereeOptionsFor = (rowKey: string) => {
    const takenElsewhere = new Set(
      transferForm.transferees.filter((t) => t.key !== rowKey && t.to_investor_id).map((t) => t.to_investor_id as string)
    )
    return (editorOptions?.investors ?? [])
      .filter((i) => i.value !== transferRow?.investors__id)
      .map((i) => ({ value: i.value, label: i.label, disabled: takenElsewhere.has(i.value) }))
  }
  const shownPosition = isBooked ? bookedSnapshot : transferPreview
  const detailTransfer = detail?.transfer ?? null
  const detailTransfereesById = useMemo(
    () => new Map((detailTransfer?.transferees ?? []).map((t) => [t.id, t])),
    [detailTransfer]
  )
  const transferSourceAmount = isBooked
    ? (detailTransfer?.prog__source_amount ?? detailTransfer?.fund_commitments__amount ?? null)
    : (transferRow?.fund_commitments__amount ?? null)
  const transferTakenAmount = transferForm.transferees.reduce((sum, t) => sum + (t.amount ?? 0), 0)
  const transferResidual = transferSourceAmount == null ? null : transferSourceAmount - transferTakenAmount
  const transferMode = isBooked ? (detailTransfer?.prog__mode ?? null) : transferModeOf(transferForm.transferees, transferSourceAmount)
  const fractionOf = (amount: number | null) =>
    amount == null || !transferSourceAmount ? null : amount / transferSourceAmount
  const pctOf = (fraction: number | null) => (fraction == null ? '-' : `${(fraction * 100).toFixed(2)}%`)

  const positionCard = shownPosition ? (
    <Descriptions
      size='small'
      column={{ xs: 1, md: 3 }}
      bordered
      title={
        <span className='text-sm font-normal text-gray-500'>
          Position at {dayjs(shownPosition.prog__as_of).format(DATE_FORMAT)}
        </span>
      }
    >
      <Descriptions.Item label='Holder'>{shownPosition.investors__name ?? '-'}</Descriptions.Item>
      <Descriptions.Item label='Commitment'>{shownPosition.fund_commitments__amount_fmt ?? '-'}</Descriptions.Item>
      <Descriptions.Item label='Uncalled'>{shownPosition.prog__uncalled_fmt ?? '-'}</Descriptions.Item>
      <Descriptions.Item label='Called'>{shownPosition.prog__called_fmt ?? '-'}</Descriptions.Item>
      <Descriptions.Item label='Paid'>{shownPosition.prog__paid_fmt ?? '-'}</Descriptions.Item>
      <Descriptions.Item label='Outstanding'>{shownPosition.prog__outstanding_fmt ?? '-'}</Descriptions.Item>
      <Descriptions.Item label='Contributions'>{shownPosition.prog__contributions_fmt ?? '-'}</Descriptions.Item>
      <Descriptions.Item label='Management fees'>{shownPosition.prog__management_fees_fmt ?? '-'}</Descriptions.Item>
      <Descriptions.Item label='Other expenses'>{shownPosition.prog__other_expenses_fmt ?? '-'}</Descriptions.Item>
      <Descriptions.Item label='Income and gains'>{shownPosition.prog__income_and_gains_fmt ?? '-'}</Descriptions.Item>
      <Descriptions.Item label='Distributions'>{shownPosition.prog__distributions_fmt ?? '-'}</Descriptions.Item>
      <Descriptions.Item label={<strong>Capital balance</strong>}>
        <strong>{shownPosition.prog__capital_balance_fmt ?? '-'}</strong>
      </Descriptions.Item>
      {transferMode === 'split' ? (
        <Descriptions.Item label='Transferable (stamped) capital' span={3}>
          {shownPosition.prog__transferable_balance_fmt ?? '-'}
          <span className='text-gray-500 text-xs ml-2'>
            what each transferee receives pro-rata; the share of unallocated fund income and expenses follows
            the commitment amounts
          </span>
        </Descriptions.Item>
      ) : null}
    </Descriptions>
  ) : transferPreviewError ? (
    <Alert type='warning' showIcon message={transferPreviewError} />
  ) : (
    <Text type='secondary'>
      {transferForm.fund_commitment_id
        ? 'Set the transfer date to see the position that transfers.'
        : 'Choose a commitment to see the position that transfers.'}
    </Text>
  )

  // what each transferee receives (split only): its fraction of the
  // position shown above
  const shareTable =
    transferMode === 'split' && shownPosition && transferForm.transferees.length > 0 ? (
      <Table
        size='small'
        pagination={false}
        rowKey='key'
        dataSource={transferForm.transferees}
        columns={[
          {
            title: 'Transferee',
            key: 'name',
            render: (_: unknown, t: TransfereeRow) => transfereeDisplayName(t)
          },
          {
            title: 'Commitment',
            key: 'amount',
            align: 'right' as const,
            render: (_: unknown, t: TransfereeRow) => (t.amount == null ? '-' : currencyText(t.amount))
          },
          {
            title: 'Share',
            key: 'share',
            align: 'right' as const,
            render: (_: unknown, t: TransfereeRow) => pctOf(fractionOf(t.amount))
          },
          {
            title: 'Called',
            key: 'called',
            align: 'right' as const,
            render: (_: unknown, t: TransfereeRow) => {
              const f = fractionOf(t.amount)
              return f == null ? '-' : currencyText(shownPosition.prog__called * f)
            }
          },
          {
            title: 'Paid',
            key: 'paid',
            align: 'right' as const,
            render: (_: unknown, t: TransfereeRow) => {
              const f = fractionOf(t.amount)
              return f == null ? '-' : currencyText(shownPosition.prog__paid * f)
            }
          },
          {
            title: 'Capital transferred',
            key: 'capital',
            align: 'right' as const,
            render: (_: unknown, t: TransfereeRow) => {
              const f = fractionOf(t.amount)
              return f == null ? '-' : currencyText(shownPosition.prog__transferable_balance * f)
            }
          }
        ]}
      />
    ) : null

  function transfereeDisplayName(t: TransfereeRow): string {
    const saved = t.id ? detailTransfereesById.get(t.id) : undefined
    if (saved?.to_investors__name) {
      return `${saved.to_investors__name}${saved.to_accounts__name ? ` · ${saved.to_accounts__name}` : ''}`
    }
    if (t.to_mode === 'new') return t.new_investor_name.trim() || 'New investor'
    const opt = (editorOptions?.investors ?? []).find((i) => i.value === t.to_investor_id)
    return opt?.label ?? '-'
  }

  const termsRow = (t: TransfereeRow) => (
    <Row gutter={16}>
      <Col xs={12} md={4}>
        <div className='mb-1 text-gray-500 text-sm'>Management fee</div>
        {readOnly ? (
          <Input value={pctText(t.management_fee_rate)} disabled readOnly />
        ) : (
          <InputNumber
            className='w-full'
            min={0}
            max={100}
            suffix='%'
            value={decimalToPercent(t.management_fee_rate)}
            onChange={(v) => updateTransferee(t.key, { management_fee_rate: percentToDecimal(v) })}
            {...thousandsProps}
          />
        )}
      </Col>
      <Col xs={12} md={4}>
        <div className='mb-1 text-gray-500 text-sm'>Carry (base)</div>
        {readOnly ? (
          <Input value={pctText(t.base_carry_rate)} disabled readOnly />
        ) : (
          <InputNumber
            className='w-full'
            min={0}
            max={100}
            suffix='%'
            value={decimalToPercent(t.base_carry_rate)}
            onChange={(v) => updateTransferee(t.key, { base_carry_rate: percentToDecimal(v) })}
            {...thousandsProps}
          />
        )}
      </Col>
      <Col xs={12} md={4}>
        <div className='mb-1 text-gray-500 text-sm'>Hurdle (base)</div>
        {readOnly ? (
          <Input value={multText(t.base_hurdle_multiple)} disabled readOnly />
        ) : (
          <InputNumber
            className='w-full'
            min={0}
            suffix='x'
            value={t.base_hurdle_multiple}
            onChange={(v) => updateTransferee(t.key, { base_hurdle_multiple: v == null ? null : Number(v) })}
            {...thousandsProps}
          />
        )}
      </Col>
      <Col xs={12} md={4}>
        <div className='mb-1 text-gray-500 text-sm'>Carry (stepped)</div>
        {readOnly ? (
          <Input value={pctText(t.stepped_carry_rate)} disabled readOnly />
        ) : (
          <InputNumber
            className='w-full'
            min={0}
            max={100}
            suffix='%'
            value={decimalToPercent(t.stepped_carry_rate)}
            onChange={(v) => updateTransferee(t.key, { stepped_carry_rate: percentToDecimal(v) })}
            {...thousandsProps}
          />
        )}
      </Col>
      <Col xs={12} md={4}>
        <div className='mb-1 text-gray-500 text-sm'>Hurdle (stepped)</div>
        {readOnly ? (
          <Input value={multText(t.stepped_hurdle_multiple)} disabled readOnly />
        ) : (
          <InputNumber
            className='w-full'
            min={0}
            suffix='x'
            value={t.stepped_hurdle_multiple}
            onChange={(v) => updateTransferee(t.key, { stepped_hurdle_multiple: v == null ? null : Number(v) })}
            {...thousandsProps}
          />
        )}
      </Col>
      {!readOnly ? (
        <Col xs={24} md={4} className='flex items-end'>
          <Button
            type='link'
            size='small'
            className='px-0'
            disabled={!transferRow}
            onClick={() => updateTransferee(t.key, termsFromCandidate(transferRow))}
          >
            Reset to transferor&apos;s terms
          </Button>
        </Col>
      ) : null}
    </Row>
  )

  // one editable row per transferee: who, how much, memo price; the terms
  // and notes sit in the expanded panel (open by default so a new row's
  // fields are visible)
  const transfereeColumns = [
    {
      title: 'Transferee',
      key: 'to',
      width: '40%',
      render: (_: unknown, t: TransfereeRow) =>
        readOnly ? (
          <span>{transfereeDisplayName(t)}</span>
        ) : (
          <Space direction='vertical' size='small' className='w-full'>
            <Radio.Group
              optionType='button'
              size='small'
              value={t.to_mode}
              options={[
                { value: 'existing', label: 'Existing investor' },
                { value: 'new', label: 'New investor entity' }
              ]}
              onChange={(e) => updateTransferee(t.key, { to_mode: e.target.value })}
            />
            {t.to_mode === 'existing' ? (
              <Select
                className='w-full'
                showSearch
                optionFilterProp='label'
                value={t.to_investor_id}
                options={transfereeOptionsFor(t.key)}
                placeholder='Select investor'
                onChange={(v: string) => updateTransferee(t.key, { to_investor_id: v })}
              />
            ) : (
              <>
                <Select
                  className='w-full'
                  showSearch
                  filterOption={false}
                  onSearch={searchAccounts}
                  loading={accountSearching}
                  options={accountOptions}
                  value={t.new_account_id}
                  placeholder='Search accounts'
                  notFoundContent={accountSearching ? 'Searching…' : 'Type to search'}
                  onChange={(v: string, option) => {
                    const label = Array.isArray(option) ? undefined : option?.label
                    updateTransferee(t.key, {
                      new_account_id: v,
                      new_investor_name: t.new_investor_name || (label ? String(label) : '')
                    })
                  }}
                />
                <Input
                  placeholder='Investor name'
                  value={t.new_investor_name}
                  onChange={(e) => updateTransferee(t.key, { new_investor_name: e.target.value })}
                />
                <Input
                  placeholder='Legal name'
                  value={t.new_investor_legal_name}
                  onChange={(e) => updateTransferee(t.key, { new_investor_legal_name: e.target.value })}
                />
                <Select
                  className='w-full'
                  allowClear
                  options={editorOptions?.classifications ?? []}
                  value={t.new_investor_classification_id}
                  placeholder='Classification'
                  onChange={(v: number | undefined) => updateTransferee(t.key, { new_investor_classification_id: v ?? null })}
                />
                {editorOptions?.fund?.funds__is_sbic ? (
                  <Select
                    className='w-full'
                    allowClear
                    options={editorOptions?.sbic_investor_types ?? []}
                    value={t.new_investor_sbic_type_id}
                    placeholder='SBIC investor type'
                    onChange={(v: number | undefined) => updateTransferee(t.key, { new_investor_sbic_type_id: v ?? null })}
                  />
                ) : null}
              </>
            )}
          </Space>
        )
    },
    {
      title: 'Amount',
      key: 'amount',
      width: '20%',
      align: 'right' as const,
      render: (_: unknown, t: TransfereeRow) =>
        readOnly ? (
          <span>{t.amount == null ? '-' : currencyText(t.amount)}</span>
        ) : (
          <InputNumber
            className='w-full'
            min={0}
            max={transferSourceAmount ?? undefined}
            value={t.amount}
            placeholder='Amount taken'
            onChange={(v) => updateTransferee(t.key, { amount: v == null ? null : Number(v) })}
            {...currencyFmt}
          />
        )
    },
    {
      title: 'Share',
      key: 'share',
      width: '10%',
      align: 'right' as const,
      render: (_: unknown, t: TransfereeRow) => pctOf(fractionOf(t.amount))
    },
    {
      title: 'Consideration (memo)',
      key: 'consideration',
      width: '20%',
      align: 'right' as const,
      render: (_: unknown, t: TransfereeRow) =>
        readOnly ? (
          <span>{t.consideration_amount == null ? '-' : currencyText(t.consideration_amount)}</span>
        ) : (
          <InputNumber
            className='w-full'
            min={0}
            value={t.consideration_amount}
            onChange={(v) => updateTransferee(t.key, { consideration_amount: v == null ? null : Number(v) })}
            {...currencyFmt}
          />
        )
    },
    ...(readOnly
      ? []
      : [
          {
            title: '',
            key: 'actions',
            width: 48,
            render: (_: unknown, t: TransfereeRow) => (
              <Button
                type='text'
                size='small'
                danger
                icon={<DeleteOutlined />}
                aria-label='Remove transferee'
                onClick={() => removeTransferee(t.key)}
              />
            )
          }
        ])
  ]

  const transferSummary = transferRow || (isBooked && detailTransfer) ? (
    <div className='text-gray-500 text-xs mt-1'>
      {transferMode == null
        ? 'Add a transferee.'
        : transferMode === 'whole'
          ? `The whole ${currencyText(transferSourceAmount ?? 0)} commitment moves to the transferee with its full history.`
          : `${currencyText(transferTakenAmount)} of ${currencyText(transferSourceAmount ?? 0)} (${pctOf(
              fractionOf(transferTakenAmount)
            )}) to ${transferForm.transferees.length} transferee${transferForm.transferees.length === 1 ? '' : 's'} as new commitments · ${
              transferResidual != null && transferResidual > 0.005
                ? `${currencyText(transferResidual)} stays with ${transferRow?.investors__name ?? detailTransfer?.from_investors__name ?? 'the seller'}`
                : `${transferRow?.investors__name ?? detailTransfer?.from_investors__name ?? 'the seller'}'s commitment ends Transferred`
            }`}
    </div>
  ) : null

  const transferSection = (
    <FormSection
      title='Transfer'
      titleColor='orange'
      scrollable={false}
      headerActions={
        readOnly || !transferForm.fund_commitment_id ? null : (
          <Button size='small' icon={<PlusOutlined />} onClick={addTransferee}>
            Add transferee
          </Button>
        )
      }
    >
      {!fundId ? (
        <Text type='secondary'>Select a fund to list its commitments.</Text>
      ) : transferError ? (
        <Alert type='error' showIcon message={transferError} />
      ) : (
        <Space direction='vertical' size='middle' className='w-full'>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <div className='mb-1 text-gray-500 text-sm'>From (commitment being assigned)</div>
              {readOnly ? (
                <Input
                  value={`${detailTransfer?.from_investors__name ?? ''}${detailTransfer?.from_accounts__name ? ` · ${detailTransfer.from_accounts__name}` : ''} · ${currencyText(transferSourceAmount ?? 0)}`}
                  disabled
                  readOnly
                />
              ) : (
                <Select
                  className='w-full'
                  showSearch
                  optionFilterProp='label'
                  loading={transferLoading}
                  value={transferForm.fund_commitment_id}
                  options={transferCandidateOptions}
                  placeholder='Select the commitment...'
                  onChange={(v: string) => chooseTransferCommitment(v)}
                />
              )}
              {transferRow && !readOnly ? (
                <div className='text-gray-500 text-xs mt-1'>
                  {transferRow.accounts__name ?? ''} · {transferRow.fund_commitment_statuses__label ?? ''} · called{' '}
                  {transferRow.prog__called_fmt ?? '-'} · paid {transferRow.prog__paid_fmt ?? '-'} · outstanding{' '}
                  {transferRow.prog__outstanding_fmt ?? '-'}
                </div>
              ) : null}
            </Col>
            <Col xs={24} md={12}>
              <div className='mb-1 text-gray-500 text-sm'>What Post does</div>
              <Input
                value={transferMode == null ? '-' : transferMode === 'whole' ? 'Whole transfer (re-point the commitment)' : 'Partial / split transfer (new commitments per transferee)'}
                disabled
                readOnly
              />
              {transferSummary}
            </Col>
          </Row>

          <div>
            <div className='mb-1 text-gray-500 text-sm'>
              Transferees
              {isBooked && detailTransfer?.prior_management_fee_rate != null ? (
                <span className='ml-2'>
                  (seller&apos;s terms: fee {pctText(detailTransfer.prior_management_fee_rate)}, carry{' '}
                  {pctText(detailTransfer.prior_base_carry_rate)} / {multText(detailTransfer.prior_base_hurdle_multiple)}, stepped{' '}
                  {pctText(detailTransfer.prior_stepped_carry_rate)} / {multText(detailTransfer.prior_stepped_hurdle_multiple)})
                </span>
              ) : null}
            </div>
            <Table
              size='small'
              columns={transfereeColumns}
              dataSource={transferForm.transferees}
              pagination={false}
              rowKey='key'
              locale={{ emptyText: transferForm.fund_commitment_id ? 'No transferees - add one' : 'Choose the commitment first' }}
              expandable={{
                defaultExpandAllRows: true,
                expandedRowKeys: transferForm.transferees.map((t) => t.key),
                showExpandColumn: false,
                expandedRowRender: (t: TransfereeRow) => (
                  <Space direction='vertical' size='small' className='w-full'>
                    <div className='text-gray-500 text-xs'>Terms for this transferee</div>
                    {termsRow(t)}
                    <Input.TextArea
                      placeholder='Notes'
                      autoSize={{ minRows: 1, maxRows: 3 }}
                      value={t.notes}
                      disabled={readOnly}
                      onChange={(e) => updateTransferee(t.key, { notes: e.target.value })}
                    />
                  </Space>
                )
              }}
            />
            {!readOnly ? (
              <div className='text-gray-500 text-xs mt-1'>
                Terms are prefilled from the transferor. Changed terms apply to future fee calculations — including
                the self-truing of earlier periods on the next fee booking. Consideration is a memo — never booked.
              </div>
            ) : null}
          </div>

          {positionCard}
          {shareTable}

          {!readOnly ? (
            <Text type='secondary'>
              {transferMode === 'split'
                ? 'Post creates a new commitment per transferee (the seller\'s dates and status, the terms above) and reduces the seller\'s commitment by the amounts taken - to Transferred when nothing is left. Each new commitment inherits its share of the seller\'s called, paid and stamped capital; statements show a transfer out for the seller and a transfer in per transferee. Nothing is journalled. Post is refused while the seller has an unpaid call as at the transfer date.'
                : 'Post re-points the commitment to the transferee as of the transfer date. Its ledger, calls, contributions and fee history stay with the commitment; statements show a transfer out for the transferor and a transfer in for the transferee. Nothing is journalled.'}
            </Text>
          ) : null}
        </Space>
      )}
    </FormSection>
  )

  // Fee capital tracking (A3.8): the part of this payment the LPs' fee
  // capital does not cover is recorded as paid from partnership assets. It
  // defaults to the remainder source (uninvested contributions); an amount
  // entered for another source (undistributed proceeds - a deemed
  // distribution) is split over the LPs pro rata. Shown only when something
  // is uncovered, reversed or entered; on a Booked transaction only when it
  // booked pairs, and it then reports what was booked (a fresh "not covered"
  // figure would mislead on a fee booked before tracking existed).
  const fundingSources = feeFundingInfo?.sources ?? []
  const fundingEntered = fundingSources
    .filter((s) => !s.is_remainder)
    .reduce((a, s) => a + Number(feeFunding[s.id] ?? 0), 0)
  const showFunding =
    tracksFeeCapital &&
    feeFundingInfo != null &&
    (readOnly
      ? feeFundingInfo.funded_total !== 0
      : feeFundingInfo.uncovered_total > 0 ||
        feeFundingInfo.reversal_total < 0 ||
        feeFundingInfo.funded_total !== 0 ||
        fundingEntered > 0)
  const fundingSection = showFunding ? (
    <FormSection
      title='Funding'
      titleColor='orange'
      scrollable={false}
      headerActions={
        staleTag ??
        (feeFundingInfo?.stale && !readOnly ? (
          <Tag color='orange' className='m-0 font-normal'>
            Position changed — save to recalculate
          </Tag>
        ) : null)
      }
    >
      <Text className='block mb-2'>
        {readOnly ? (
          <>
            Booked as paid from partnership assets:{' '}
            <Text strong>{currencyText(feeFundingInfo?.funded_total ?? 0)}</Text>
          </>
        ) : (
          <>
            Fees not covered by fee capital:{' '}
            <Text strong>{currencyText(feeFundingInfo?.uncovered_total ?? 0)}</Text>
          </>
        )}
        {!readOnly && (feeFundingInfo?.reversal_total ?? 0) < 0 ? (
          <>
            {' · '}reversed (fee capital now covers earlier payments):{' '}
            <Text strong>{currencyText(feeFundingInfo?.reversal_total ?? 0)}</Text>
          </>
        ) : null}
      </Text>
      <Table
        size='small'
        pagination={false}
        rowKey='id'
        dataSource={fundingSources.filter((s) => s.allowed || s.allocated !== 0)}
        columns={[
          {
            title: 'Source',
            dataIndex: 'label',
            render: (v: string, s: any) => (
              <Tooltip title={s.description ?? undefined}>
                <span>
                  {v}
                  {s.is_deemed_distribution ? (
                    <Tag className='ml-2 font-normal'>Deemed distribution</Tag>
                  ) : null}
                </span>
              </Tooltip>
            )
          },
          {
            title: 'Amount',
            width: 180,
            align: 'right' as const,
            render: (_: unknown, s: any) =>
              readOnly ? (
                <Text type='secondary'>{s.amount_input != null ? currencyText(s.amount_input) : '-'}</Text>
              ) : s.is_remainder ? (
                <Text type='secondary'>
                  {currencyText(Math.max(0, (feeFundingInfo?.uncovered_total ?? 0) - fundingEntered))} (the rest)
                </Text>
              ) : (
                <InputNumber
                  size='small'
                  className='w-full'
                  value={feeFunding[s.id] ?? null}
                  {...currencyFmt}
                  placeholder='0'
                  disabled={readOnly || !s.allowed}
                  onChange={(v) => {
                    setFeeFunding((prev) => ({ ...prev, [s.id]: v == null ? null : Number(v) }))
                    markDirty()
                  }}
                />
              )
          },
          {
            title: 'Booked in this transaction',
            dataIndex: 'allocated',
            width: 180,
            align: 'right' as const,
            render: (v: number) => (v !== 0 ? currencyText(v) : '-')
          }
        ]}
      />
      <Text type='secondary' className='block mt-2'>
        Each source is split over the LPs pro rata to their uncovered fees and booked as an equity pair on the
        LP (Dr the source / Cr Capital contribution - fees) - zero-sum within its capital account. Uninvested
        contributions debit the LP's own investment / expense capital; undistributed proceeds book a deemed
        distribution and re-contribution, and assume the amount is recallable. Save to apply.
      </Text>
    </FormSection>
  ) : null

  const lineItemsSection = (
    <FormSection
      title='Line Items'
      titleColor='purple'
      scrollable={false}
      headerActions={
        readOnly || feeItemManaged ? null : (
          <Button size='small' icon={<PlusOutlined />} onClick={addItem}>
            Add Item
          </Button>
        )
      }
    >
      <Table size='small' columns={itemColumns} dataSource={items} pagination={false} rowKey='key' />
      {feeItemManaged && !readOnly ? (
        <Text type='secondary' className='block mt-2'>
          The fee engine sets the item and amount. A description typed here is kept on recalculation
          and used on every fee journal; clear it to restore the default.
        </Text>
      ) : null}
    </FormSection>
  )

  const detailsContent = (
    <div className='flex flex-col gap-3'>
      <FormSection title='Details' titleColor='blue' scrollable={false}>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={8}>
            <div className='mb-1 text-gray-500 text-sm'>Fund</div>
            <FundSelect
              className='w-full'
              value={fundId}
              placeholder='Select fund...'
              status={fieldErrors.fund ? 'error' : undefined}
              disabled={readOnly || !isNewRecord()}
              onChange={(v) => {
                setFundId(v)
                setFieldErrors((prev) => ({ ...prev, fund: false }))
                markDirty()
              }}
            />
            {fieldErrors.fund ? (
              <div className='text-red-500 text-xs mt-1'>Fund is required</div>
            ) : null}
          </Col>
          <Col xs={24} md={8}>
            <div className='mb-1 text-gray-500 text-sm'>Type</div>
            <Select
              className='w-full'
              value={typeId}
              options={typeOptions}
              placeholder='Select type...'
              status={fieldErrors.type ? 'error' : undefined}
              disabled={readOnly}
              onChange={(v) => {
                setTypeId(v)
                setFieldErrors((prev) => ({ ...prev, type: false }))
                markDirty()
              }}
            />
            {fieldErrors.type ? (
              <div className='text-red-500 text-xs mt-1'>Type is required</div>
            ) : null}
            {isClosingType ? (
              <div className='text-gray-500 text-xs mt-1'>Closes the ticked commitments as of the closing date</div>
            ) : null}
            {isTransferType ? (
              <div className='text-gray-500 text-xs mt-1'>Re-points the commitment to the transferee as of the transfer date</div>
            ) : null}
            {selectedType && (selectedType.debit_item_id || selectedType.credit_item_id) ? (
              <div className='text-gray-500 text-xs mt-1'>
                Books Dr{' '}
                {accItems.find((i: any) => i.id === selectedType.debit_item_id)?.label ?? 'per line item'}
                {' / Cr '}
                {accItems.find((i: any) => i.id === selectedType.credit_item_id)?.label ?? 'per line item'}
              </div>
            ) : null}
          </Col>
          <Col xs={24} md={8}>
            <div className='mb-1 text-gray-500 text-sm'>
              {isClosingType ? 'Closing date' : isTransferType ? 'Transfer date' : 'Date'}
            </div>
            <DatePicker
              className='w-full'
              value={transactionDate ? dayjs(transactionDate) : null}
              format={DATE_FORMAT}
              allowClear={false}
              status={fieldErrors.date ? 'error' : undefined}
              disabled={readOnly}
              onChange={(d) => {
                setTransactionDate(d ? d.format('YYYY-MM-DD') : null)
                setFieldErrors((prev) => ({ ...prev, date: false }))
                markDirty()
              }}
            />
            {fieldErrors.date ? (
              <div className='text-red-500 text-xs mt-1'>Date is required</div>
            ) : null}
          </Col>
          {isFeeType ? (
            <>
              {feePeriodCustom ? (
                <>
                  <Col xs={24} md={8}>
                    <div className='mb-1 text-gray-500 text-sm'>Fee Period From</div>
                    <DatePicker
                      className='w-full'
                      value={feePeriodStart ? dayjs(feePeriodStart) : null}
                      format={DATE_FORMAT}
                      disabled={readOnly}
                      onChange={(d) => {
                        setFeePeriodStart(d ? d.format('YYYY-MM-DD') : null)
                        markDirty()
                      }}
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <div className='mb-1 text-gray-500 text-sm'>Fee Period To</div>
                    <DatePicker
                      className='w-full'
                      value={feePeriodEnd ? dayjs(feePeriodEnd) : null}
                      format={DATE_FORMAT}
                      disabled={readOnly}
                      onChange={(d) => {
                        setFeePeriodEnd(d ? d.format('YYYY-MM-DD') : null)
                        markDirty()
                      }}
                    />
                  </Col>
                </>
              ) : (
                <Col xs={24} md={8}>
                  <div className='mb-1 text-gray-500 text-sm'>Fee Period</div>
                  <Select
                    className='w-full'
                    value={singleQuarterOf(feePeriodStart, feePeriodEnd)}
                    options={quarterOptions}
                    placeholder='Select quarter...'
                    showSearch
                    optionFilterProp='label'
                    allowClear
                    disabled={readOnly}
                    onChange={(v: string | undefined) => {
                      if (v) {
                        const [year, q] = v.split('-Q')
                        setFeePeriodStart(quarterStartDate(Number(year), Number(q)).format('YYYY-MM-DD'))
                        setFeePeriodEnd(quarterEndDate(Number(year), Number(q)).format('YYYY-MM-DD'))
                      } else {
                        setFeePeriodStart(null)
                        setFeePeriodEnd(null)
                      }
                      markDirty()
                    }}
                  />
                </Col>
              )}
              <Col xs={24} md={feePeriodCustom ? 8 : 16}>
                <div className='mb-1 flex items-center gap-2'>
                  <Switch
                    size='small'
                    checked={feePeriodCustom}
                    disabled={readOnly}
                    onChange={(checked) => {
                      if (!checked) {
                        // snap back to the quarter containing the end date
                        if (feePeriodEnd) {
                          const d = dayjs(feePeriodEnd)
                          const q = Math.floor(d.month() / 3) + 1
                          setFeePeriodStart(quarterStartDate(d.year(), q).format('YYYY-MM-DD'))
                          setFeePeriodEnd(quarterEndDate(d.year(), q).format('YYYY-MM-DD'))
                          markDirty()
                        }
                      }
                      setFeePeriodCustom(checked)
                    }}
                  />
                  <span className='text-gray-500 text-sm'>Custom dates</span>
                </div>
                <div className='text-gray-500 text-xs pt-1'>
                  Books total fees due through the period end minus fees already
                  booked, per commitment - prior gaps catch up automatically.
                </div>
              </Col>
            </>
          ) : null}
          {isClosingType ? (
            <Col xs={24} md={8}>
              <div className='mb-1 text-gray-500 text-sm'>Amount</div>
              <Input value={currencyText(closingSelectedTotal)} disabled readOnly />
              <div className='text-gray-500 text-xs mt-1'>Sum of the ticked commitments</div>
            </Col>
          ) : null}
          {isTransferType ? (
            <Col xs={24} md={8}>
              <div className='mb-1 text-gray-500 text-sm'>Amount</div>
              <Input
                value={currencyText(
                  isBooked
                    ? (detail?.transfer?.prog__source_amount ?? detail?.transfer?.fund_commitments__amount ?? 0)
                    : (transferRow?.fund_commitments__amount ?? 0)
                )}
                disabled
                readOnly
              />
              <div className='text-gray-500 text-xs mt-1'>The commitment being assigned</div>
            </Col>
          ) : null}
          <Col xs={24}>
            <div className='mb-1 text-gray-500 text-sm'>Description</div>
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 3 }}
              value={description}
              placeholder={isClosingType ? 'e.g. First close' : isTransferType ? 'e.g. Harbor sells to Redwood' : undefined}
              disabled={readOnly}
              onChange={(e) => {
                setDescription(e.target.value)
                markDirty()
              }}
            />
          </Col>
        </Row>
      </FormSection>

      {isClosingType ? commitmentsSection : isTransferType ? transferSection : lineItemsSection}

      {/* a closing of a fund on the Financial year basis: what the admitted
          partners take extra of per tax category (informational, PR 3) */}
      {isClosingType ? (
        <ClosingEarlierYearsSection
          transactionId={detailId}
          refreshKey={`${detailStatusLabel ?? ''}|${transactionDate ?? ''}|${savedClosingCount}`}
        />
      ) : null}

      {paysFees && (detail?.fee_allocations?.length ?? 0) > 0 ? (
        <FormSection
          title='Fee Allocations'
          titleColor='orange'
          scrollable={false}
          headerActions={
            <>
              {staleTag}
              <Button
              size='small'
              icon={<DownloadOutlined />}
              onClick={async () => {
                const token = getAccessToken(store.getState().auth)
                const allocations = detail?.fee_allocations ?? []
                const res = await exportJsonToExcel(
                  {
                    data: allocations.map((r) => ({
                      Investor: r.investors__name ?? '',
                      Commitment: r.fund_commitments__amount ?? 0,
                      'Due to Date': r.prog__due_to_date ?? 0,
                      ...(hasFeeCrossCredit ? { 'Cross-credit': r.prog__cross_credit_to_date ?? 0 } : {}),
                      'Booked Before': r.prog__booked_before ?? 0,
                      Calculated: r.calculated_amount ?? 0,
                      Override: feeOverrides[r.fund_commitment_id] ?? r.override_amount ?? '',
                      Effective: Number(
                        feeOverrides[r.fund_commitment_id] ?? r.calculated_amount ?? 0
                      ),
                      ...(hasLeverageFee ? { 'Fee on Leverage': r.prog__leverage_fee ?? 0 } : {}),
                      ...(tracksFeeCapital
                        ? {
                            'Fee Capital Before': r.prog__fee_capital_before ?? 0,
                            'Covered by Fee Capital': r.prog__covered_by_fee_capital ?? 0,
                            'From Partnership Assets': r.prog__from_assets ?? 0,
                            'Fee Capital After': r.prog__fee_capital_after ?? 0
                          }
                        : {})
                    })),
                    columns: [
                      'Investor',
                      'Commitment',
                      'Due to Date',
                      ...(hasFeeCrossCredit ? ['Cross-credit'] : []),
                      'Booked Before',
                      'Calculated',
                      'Override',
                      'Effective',
                      ...(hasLeverageFee ? ['Fee on Leverage'] : []),
                      ...(tracksFeeCapital
                        ? ['Fee Capital Before', 'Covered by Fee Capital', 'From Partnership Assets', 'Fee Capital After']
                        : [])
                    ],
                    filename: `fee_allocations_${detail?.transaction?.fund_name ?? 'fund'}`
                  },
                  token
                )
                if (!res.success) showErrorToast(res.error || 'Export failed')
              }}
            >
              Export
            </Button>
            </>
          }
        >
          <Table
            size='small'
            pagination={false}
            rowKey='fund_commitment_id'
            dataSource={detail?.fee_allocations ?? []}
            columns={[
              {
                title: 'Investor',
                dataIndex: 'investors__name',
                render: (v: string) => v ?? '-'
              },
              {
                title: 'Commitment',
                dataIndex: 'fund_commitments__amount',
                width: 140,
                align: 'right' as const,
                render: (v: number | null) => currencyText(v)
              },
              {
                title: 'Due to date',
                dataIndex: 'prog__due_to_date',
                width: 130,
                align: 'right' as const,
                render: (v: number | null) => (v != null ? currencyText(v) : '-')
              },
              // a primary fund's § 11.9(b) credit, so its rows still add up
              ...(hasFeeCrossCredit
                ? [
                    {
                      title: 'Cross-credit',
                      dataIndex: 'prog__cross_credit_to_date',
                      width: 130,
                      align: 'right' as const,
                      render: (v: number | null) => (v != null ? currencyText(v) : '-')
                    }
                  ]
                : []),
              {
                // booked before THIS transaction (never its own journals):
                // due to date - cross-credit - booked before = calculated,
                // Draft or Booked
                title: 'Booked before',
                dataIndex: 'prog__booked_before',
                width: 130,
                align: 'right' as const,
                render: (v: number | null) => (v != null ? currencyText(v) : '-')
              },
              {
                title: 'Calculated',
                dataIndex: 'calculated_amount',
                width: 140,
                align: 'right' as const,
                render: (v: number | null) => currencyText(v)
              },
              {
                title: 'Override',
                dataIndex: 'override_amount',
                width: 160,
                align: 'right' as const,
                render: (_: unknown, row: any) => (
                  <InputNumber
                    size='small'
                    className='w-full'
                    value={feeOverrides[row.fund_commitment_id] ?? null}
                    {...currencyFmt}
                    placeholder='-'
                    disabled={readOnly}
                    onChange={(v) => {
                      setFeeOverrides((prev) => ({
                        ...prev,
                        [row.fund_commitment_id]: v == null ? null : Number(v)
                      }))
                      markDirty()
                    }}
                  />
                )
              },
              {
                title: 'Effective',
                dataIndex: 'effective_amount',
                width: 140,
                align: 'right' as const,
                render: (_: unknown, row: any) => {
                  const eff = feeOverrides[row.fund_commitment_id] ?? row.calculated_amount
                  return eff != null ? <Text strong>{currencyText(Number(eff))}</Text> : '-'
                }
              },
              // the part of the fee that is on assumed leverage (paid from
              // leverage capital, outside fee capital) - as at the last save
              ...(hasLeverageFee
                ? [
                    {
                      title: 'Fee on leverage',
                      dataIndex: 'prog__leverage_fee',
                      width: 130,
                      align: 'right' as const,
                      render: (v: number | null) => (v != null ? currencyText(v) : '-')
                    }
                  ]
                : []),
              // fee capital tracking (A3.8), per the LP's tracking unit (its
              // subscription in a consolidated fund) - as at the last save
              ...(tracksFeeCapital
                ? [
                    {
                      title: 'Fee capital before',
                      dataIndex: 'prog__fee_capital_before',
                      width: 130,
                      align: 'right' as const,
                      render: (v: number | null) => (v != null ? currencyText(v) : '-')
                    },
                    {
                      title: 'Covered by fee capital',
                      dataIndex: 'prog__covered_by_fee_capital',
                      width: 140,
                      align: 'right' as const,
                      render: (v: number | null) => (v != null ? currencyText(v) : '-')
                    },
                    {
                      title: 'From partnership assets',
                      dataIndex: 'prog__from_assets',
                      width: 150,
                      align: 'right' as const,
                      render: (v: number | null) =>
                        v != null && v !== 0 ? <Text strong>{currencyText(v)}</Text> : '-'
                    },
                    {
                      title: 'Fee capital after',
                      dataIndex: 'prog__fee_capital_after',
                      width: 130,
                      align: 'right' as const,
                      render: (v: number | null) => (v != null ? currencyText(v) : '-')
                    }
                  ]
                : [])
            ]}
            summary={() => {
              const rows = detail?.fee_allocations ?? []
              const dueTotal = rows.reduce((a, r) => a + (r.prog__due_to_date ?? 0), 0)
              const crossTotal = rows.reduce((a, r) => a + (r.prog__cross_credit_to_date ?? 0), 0)
              const bookedTotal = rows.reduce((a, r) => a + (r.prog__booked_before ?? 0), 0)
              const calcTotal = rows.reduce((a, r) => a + (r.calculated_amount ?? 0), 0)
              const effTotal = rows.reduce(
                (a, r) => a + Number(feeOverrides[r.fund_commitment_id] ?? r.calculated_amount ?? 0),
                0
              )
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row className='font-medium'>
                    <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} />
                    <Table.Summary.Cell index={2} align='right'>
                      {currencyText(dueTotal)}
                    </Table.Summary.Cell>
                    {hasFeeCrossCredit ? (
                      <Table.Summary.Cell index={3} align='right'>
                        {currencyText(crossTotal)}
                      </Table.Summary.Cell>
                    ) : null}
                    <Table.Summary.Cell index={hasFeeCrossCredit ? 4 : 3} align='right'>
                      {currencyText(bookedTotal)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={hasFeeCrossCredit ? 5 : 4} align='right'>
                      {currencyText(calcTotal)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={hasFeeCrossCredit ? 6 : 5} />
                    <Table.Summary.Cell index={hasFeeCrossCredit ? 7 : 6} align='right'>
                      {currencyText(effTotal)}
                    </Table.Summary.Cell>
                    {hasLeverageFee ? (
                      <Table.Summary.Cell index={hasFeeCrossCredit ? 8 : 7} align='right'>
                        {currencyText(rows.reduce((a, r) => a + (r.prog__leverage_fee ?? 0), 0))}
                      </Table.Summary.Cell>
                    ) : null}
                    {tracksFeeCapital
                      ? (
                          [
                            'prog__fee_capital_before',
                            'prog__covered_by_fee_capital',
                            'prog__from_assets',
                            'prog__fee_capital_after'
                          ] as const
                        ).map((key, i) => (
                          <Table.Summary.Cell
                            key={key}
                            index={(hasFeeCrossCredit ? 8 : 7) + (hasLeverageFee ? 1 : 0) + i}
                            align='right'
                          >
                            {currencyText(rows.reduce((a, r) => a + (r[key] ?? 0), 0))}
                          </Table.Summary.Cell>
                        ))
                      : null}
                  </Table.Summary.Row>
                </Table.Summary>
              )
            }}
          />
          <Text type='secondary' className='block mt-2'>
            Calculated = total fees due through the period end
            {hasFeeCrossCredit ? ' minus the cross-credit' : ''} minus fees booked before this
            transaction, so each booking also catches up prior gaps (negative amounts book as fee
            credits). Overrides persist across recomputes; journals book the effective amount. Save
            to apply.
            {tracksFeeCapital
              ? ' Fee capital: what each LP (its consolidated commitment, in a consolidated fund) contributed for fees, less the fees it has paid; any part of this payment it does not cover is recorded as paid from partnership assets (see Funding).'
              : ''}
            {hasLeverageFee
              ? ' Fee on leverage: the part of the fee charged on assumed leverage. It is paid from leverage capital, so it uses no fee capital and is never paid from partnership assets; the fee capital columns cover Effective minus Fee on leverage.'
              : ''}
          </Text>
        </FormSection>
      ) : null}

      {fundingSection}

      {!journalsOnSeparateTab && !isClosingType && !isTransferType ? journalPreview : null}
    </div>
  )

  if (loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Spin />
      </div>
    )
  }

  if (notFound) {
    return (
      <div className='p-10 text-center text-gray-500'>
        Fund transaction not found.
        <div className='mt-3'>
          <Button onClick={() => navigate(LIST_PATH)}>Back to Fund Transactions</Button>
        </div>
      </div>
    )
  }

  return (
    <div className='h-full w-full min-h-0 flex flex-col bg-gray-50 p-3 overflow-hidden'>
      {modalContextHolder}
      <EditContributionModal
        contributionId={editingContributionId}
        onClose={() => setEditingContributionId(null)}
        onSaved={() => {
          // the date and the amount both move, and the list tab behind this
          // form stays mounted - it only refetches on a filter/sort change
          void (async () => {
            const fresh = await refetchDetail()
            if (!detail?.transaction?.id || !fresh) return
            dispatch(
              updateFundTransactionRecord({
                transactionId: detail.transaction.id,
                updates: {
                  fund_transactions__transaction_date: fresh.transaction.transaction_date,
                  fund_transactions__transaction_date_fmt: dayjs(fresh.transaction.transaction_date).format(
                    'MM/DD/YYYY'
                  )
                }
              })
            )
          })()
        }}
      />
      <Card size='small' className='flex-1 min-h-0 flex flex-col h-full overflow-hidden' classNames={{ body: 'h-full p-3 overflow-y-auto' }}>
        <div className='flex items-center justify-between mb-3 flex-wrap gap-2'>
          <Space align='center'>
            <Title level={5} className='!mb-0'>
              {isNewRecord()
                ? isClosingType
                  ? 'New Closing'
                  : isTransferType
                    ? 'New Transfer of Interest'
                    : 'New Fund Transaction'
                : `${detail?.transaction.transaction_type_label ?? 'Transaction'} — ${detail?.transaction.fund_name ?? ''}`}
            </Title>
            {detail?.transaction?.status_label ? (
              <Tag color={isBooked ? 'green' : 'blue'}>{detail.transaction.status_label}</Tag>
            ) : null}
          </Space>
          <Space>
            {/* Owner spec: primary rightmost; destructive separated leftmost. */}
            {!isNewRecord() && !isBooked ? (
              <Button danger onClick={handleDelete}>
                Delete
              </Button>
            ) : null}
            {isBooked && contributionId ? (
              <Button onClick={() => setEditingContributionId(contributionId)}>Edit payment</Button>
            ) : null}
            {isBooked && orchestratedCallId ? (
              <Tooltip title='This transaction books with its capital call — revert the call to Draft there'>
                <Button
                  onClick={() =>
                    openFormInTab(TABLE_CAPITAL_CALLS, orchestratedCallId, 'Capital Call')
                  }
                >
                  Open capital call
                </Button>
              </Tooltip>
            ) : null}
            {isBooked && orchestratedDistributionId ? (
              <Tooltip title='This transaction books with its distribution — revert the distribution to Draft there'>
                <Button
                  onClick={() =>
                    openFormInTab(TABLE_DISTRIBUTIONS, orchestratedDistributionId, 'Distribution')
                  }
                >
                  Open distribution
                </Button>
              </Tooltip>
            ) : null}
            {isBooked && !contributionId && !orchestratedCallId && !orchestratedDistributionId ? (
              <Button loading={actionLoading} onClick={handleUnbook}>
                Unbook
              </Button>
            ) : null}
            {!readOnly && (isNewRecord() || dirty) ? (
              <Button type='primary' loading={saving} onClick={handleSave}>
                {isNewRecord() ? 'Create' : isFeeType ? 'Save & Recalculate' : 'Save'}
              </Button>
            ) : null}
            {!isNewRecord() && !isBooked && !dirty ? (
              <Tooltip
                title={
                  isClosingType && !canPost
                    ? 'Tick at least one commitment and save before posting'
                    : isTransferType && !canPost
                      ? 'Choose the commitment and the transferee and save before posting'
                      : undefined
                }
              >
                <span>
                  <Button type='primary' disabled={!canPost} loading={actionLoading} onClick={handleBook}>
                    Post
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </Space>
        </div>

        {journalsOnSeparateTab && !isClosingType && !isTransferType ? (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              { key: 'details', label: 'Details', children: detailsContent },
              { key: 'journals', label: 'Journals', children: journalPreview }
            ]}
          />
        ) : (
          detailsContent
        )}
      </Card>
    </div>
  )
}
